import { useState, useRef, useEffect } from "react";
import LogoChat from "../../assets/images/CHATDiligent.png";

function FloatingChatButton({ onClick }) {
  const [shimmerKey, setShimmerKey] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const isAnimatingRef = useRef(false);
  const svgRef = useRef(null);

  useEffect(() => {
    if (shimmerKey > 0 && svgRef.current) {
      // Find all animate elements and restart them
      const animateElements = svgRef.current.querySelectorAll("animate");
      animateElements.forEach((anim, idx) => {
        try {
          // Reset animation to beginning first
          if (anim.endElement) {
            anim.endElement();
          }
          // Small delay to ensure reset takes effect
          setTimeout(() => {
            anim.beginElement();
          }, 10);
        } catch (e) {
          console.error("❌ Error starting animation:", e);
        }
      });
    }
  }, [shimmerKey]);

  const handleMouseEnter = () => {
    setIsHovered(true);

    // Only trigger if not currently animating
    if (!isAnimatingRef.current) {
      const timestamp = Date.now();
      isAnimatingRef.current = true;
      setShimmerKey(timestamp);

      // Reset flag after animation completes (6 seconds)
      setTimeout(() => {
        isAnimatingRef.current = false;
      }, 6000);
    }
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  const handleClick = () => {
    onClick();
  };

  const gradientId = `grad-${shimmerKey}`;

  return (
    <button
      type="button"
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className="fixed bottom-6 right-6 z-50 bg-white shadow-lg border border-gray-200 cursor-pointer rounded-full overflow-hidden transition-all duration-300 ease-in-out"
      style={{
        width: isHovered ? "222px" : "60px",
        height: "60px",
        paddingLeft: "16px",
        paddingRight: "16px",
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-start",
      }}
      aria-label="ChatDiligent"
      title="ChatDiligent"
    >
      {/* Logo - slides in from left */}
      <div
        className="overflow-hidden transition-all duration-300 ease-in-out flex-shrink-0"
        style={{
          width: isHovered ? "150px" : "0",
        }}
      >
        <img
          src={LogoChat}
          alt="ChatDiligent"
          className="h-7"
          style={{
            width: "150px",
            minWidth: "150px",
            objectFit: "contain",
          }}
        />
      </div>

      {/* Spacer that grows to push icon to the right position */}
      <div
        className="transition-all duration-300 ease-in-out"
        style={{
          width: isHovered ? "12px" : "0",
        }}
      />

      {/* Chat icon with shimmer - stays fixed relative to right edge */}
      <svg
        ref={svgRef}
        className="h-7 w-7 flex-shrink-0"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth="1.5"
      >
        <defs>
          <linearGradient
            id={gradientId}
            x1="-100%"
            y1="200%"
            x2="100%"
            y2="0%"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0%" stopColor="#6036C4" stopOpacity="1" />
            <stop offset="20%" stopColor="#04C4FF" stopOpacity="1" />
            <stop offset="40%" stopColor="#0ACC59" stopOpacity="1" />
            <stop offset="60%" stopColor="#FFE600" stopOpacity="1" />
            <stop offset="80%" stopColor="#F15B2B" stopOpacity="1" />
            <stop offset="100%" stopColor="#6036C4" stopOpacity="1" />
            <animate
              attributeName="x1"
              from="-100%"
              to="200%"
              begin={shimmerKey === 0 ? "0s" : "indefinite"}
              dur="6s"
              repeatCount="1"
              fill="freeze"
            />
            <animate
              attributeName="y1"
              from="200%"
              to="-100%"
              begin={shimmerKey === 0 ? "0s" : "indefinite"}
              dur="6s"
              repeatCount="1"
              fill="freeze"
            />
            <animate
              attributeName="x2"
              from="100%"
              to="400%"
              begin={shimmerKey === 0 ? "0s" : "indefinite"}
              dur="6s"
              repeatCount="1"
              fill="freeze"
            />
            <animate
              attributeName="y2"
              from="0%"
              to="-300%"
              begin={shimmerKey === 0 ? "0s" : "indefinite"}
              dur="6s"
              repeatCount="1"
              fill="freeze"
            />
          </linearGradient>
        </defs>
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          stroke={`url(#${gradientId})`}
          d="M8.625 12a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H8.25m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H12m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 0 1-2.555-.337A5.972 5.972 0 0 1 5.41 20.97a5.969 5.969 0 0 1-.474-.065 4.48 4.48 0 0 0 .978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25Z"
        />
      </svg>
    </button>
  );
}

export default FloatingChatButton;
